// Context Pack types and Zod schemas (ARCHITECTURE.md §4.7).
//
// A ContextPack is the ranked, token-budget-constrained bundle of source that
// generateContextPack() hands to a coding agent. Each ContextEntry carries a
// relevance score, the content mode under which it was rendered (full file,
// a line section, exported signatures only, or a bare reference), and a human
// readable reason for inclusion. ContextMetadata records the budget accounting
// (tokens, files included / excluded / referenced) and artifact coverage.
//
// As elsewhere in the codebase, the inferred Zod types are the compile-time
// source of truth; the schemas provide runtime validation at trust boundaries
// (cache deserialization, plugin input).

import { z } from "zod";

// ---------------------------------------------------------------------------
// ContentMode
// ---------------------------------------------------------------------------

export const CONTENT_MODES = [
  "full", // entire file content
  "section", // specific line range (see ContextEntry.section)
  "symbol-section", // symbol-scoped line ranges (see ContextEntry.sections)
  "signature", // exported function/class/type signatures only
  "reference", // path and artifact ID only — no content
] as const;

export const ContentModeSchema = z.enum(CONTENT_MODES);

export type ContentMode = z.infer<typeof ContentModeSchema>;

// ---------------------------------------------------------------------------
// ContextEntry
// ---------------------------------------------------------------------------

// A line range within a file, used when contentMode is "section" or
// "symbol-section". Lines are 1-based and inclusive; endLine must not precede
// startLine. When the section corresponds to a symbol scope (§6.1), `symbolId`
// and `symbolName` identify it; both are optional for backward compatibility
// with plain line-range sections.
export const ContextSectionSchema = z
  .object({
    startLine: z.number().int().positive(),
    endLine: z.number().int().positive(),
    /** Symbol id (filePath#name) when this section is a symbol scope. */
    symbolId: z.string().optional(),
    /** Symbol display name when this section is a symbol scope. */
    symbolName: z.string().optional(),
  })
  .refine((s) => s.endLine >= s.startLine, {
    message: "endLine must be greater than or equal to startLine",
  });

export type ContextSection = z.infer<typeof ContextSectionSchema>;

// A single file (or fragment) included in the pack.
export const ContextEntrySchema = z.object({
  path: z.string(),
  artifactId: z.string().optional(),
  /** Relevance to the task, in the range 0.0 – 1.0. */
  relevanceScore: z.number().min(0).max(1),
  contentMode: ContentModeSchema,
  /** File content (possibly truncated); empty for "reference" entries. */
  content: z.string(),
  section: ContextSectionSchema.optional(),
  /**
   * Symbol-scoped sections when contentMode is "symbol-section": the line
   * ranges (with symbol identity) extracted from the file, sorted by line and
   * non-overlapping.
   */
  sections: z.array(ContextSectionSchema).optional(),
  /** Why this file is included. */
  reason: z.string(),
});

export type ContextEntry = z.infer<typeof ContextEntrySchema>;

// ---------------------------------------------------------------------------
// ContextMetadata
// ---------------------------------------------------------------------------

export const ContextMetadataSchema = z.object({
  totalTokens: z.number().int().nonnegative(),
  budgetTokens: z.number().int().nonnegative(),
  filesIncluded: z.number().int().nonnegative(),
  filesExcluded: z.number().int().nonnegative(),
  /** Reference-only entries (no content). */
  filesReferenced: z.number().int().nonnegative(),
  artifactsCovered: z.array(z.string()),
  /** Contract validations implicated by the change (§8.4 overlay). */
  affectedValidations: z.array(z.string()).optional(),
  /** Owner agents implicated by the change (§8.4 overlay). */
  affectedAgents: z.array(z.string()).optional(),
  /** Workflows implicated by the change (§8.4 overlay). */
  affectedWorkflows: z.array(z.string()).optional(),
  /** ISO-8601 timestamp of when the pack was generated. */
  generatedAt: z.string(),
});

export type ContextMetadata = z.infer<typeof ContextMetadataSchema>;

// ---------------------------------------------------------------------------
// ContextPack
// ---------------------------------------------------------------------------

export const ContextPackSchema = z.object({
  taskId: z.string().optional(),
  entries: z.array(ContextEntrySchema),
  metadata: ContextMetadataSchema,
});

export type ContextPack = z.infer<typeof ContextPackSchema>;
